import { useRef, useState } from 'react';
import calApi from '../api/calApi';
import { logBookingError } from '../loggingUtils';
import { BookerContextType, CalEventData } from '../types';
import { useGlobalCache } from './useGlobalCache';

export const useFetchCalEvents = (bookerContext: BookerContextType) => {
    const abortControllerRef = useRef<AbortController | null>(null);
    const globalCache = useGlobalCache();
    const [isLoading, setIsLoading] = useState(true);

    const fetchCalEvents = async ({
        organizationId,
        teamId,
    }: {
        organizationId: number;
        teamId: number;
    }) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            const queryKey = ['cal-events', organizationId, teamId];
            const cachedCalEvents = globalCache.get<CalEventData[]>(queryKey);
            if (cachedCalEvents) {
                return cachedCalEvents;
            }

            setIsLoading(true);

            const calEvents = await calApi.getTeamEvents({
                organizationId,
                teamId,
            });
            globalCache.set(queryKey, calEvents);
            return calEvents;
        } catch (error) {
            // Don't log errors for aborted requests
            if (!abortController.signal.aborted) {
                logBookingError('Failed to fetch cal events', {
                    errorMessage: (error as Error).message,
                    additionalInfoToLog: bookerContext,
                });
            }
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoading(false);
            }
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
        }
    };

    return { isLoading, fetchCalEvents };
};
